import React from "react";
import { Button, CircularProgress, Box, IconButton } from "@mui/material";
import "./index.css";

interface CustomButtonProps {
  isLoading?: boolean;
  disabled?: boolean;
  variant?: "text" | "outlined" | "contained";
  type?: "button" | "submit" | "reset";
  onClick?: () => void;
  className?: string;
  icon?: React.ElementType;
  iconColor?: string;
  text?: string;
  color?: "inherit" | "primary" | "secondary" | "success" | "error" | "info" | "warning";
  size?: "small" | "medium" | "large";
  fullWidth?: boolean;
  sx?: any;
  [x: string]: any;
}

const CustomButton: React.FC<CustomButtonProps> = (props) => {
  const { 
    isLoading, 
    disabled, 
    variant = "contained", 
    type, 
    icon: Icon, 
    iconColor, 
    text,
    color = "primary",
    size = "medium",
    fullWidth = false,
    sx,
    ...rest 
  } = props;

  const renderIcon = () => {
    if (Icon) {
      return React.createElement(Icon, {
        sx: { 
          mr: text ? 1 : 0,
          color: iconColor
        },
        fontSize: size === "small" ? "small" : size === "large" ? "large" : "medium"
      });
    }
    return null;
  };

  const renderText = () => {
    if (text) {
      return <span>{text}</span>;
    }
    return null;
  };

  // If only icon without text, render as icon button without background
  if (Icon && !text) {
    return (
      <IconButton
        onClick={props.onClick}
        disabled={disabled || isLoading}
        color={color}
        size={size}
        sx={{
          ...sx
        }}
        {...rest}
      >
        {isLoading ? (
          <CircularProgress
            size={size === "small" ? 20 : size === "large" ? 32 : 24}
            sx={{ color: "inherit" }}
          />
        ) : (
          renderIcon()
        )}
      </IconButton>
    );
  }

  return (
    <Button
      variant={variant}
      type={type}
      disabled={disabled || isLoading}
      color={color}
      size={size}
      fullWidth={fullWidth}
      sx={{
        position: "relative",
        ...sx
      }}
      {...rest}
    >
      {isLoading && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <CircularProgress
            size={size === "small" ? 20 : size === "large" ? 32 : 24}
            sx={{
              color: "inherit"
            }}
          />
        </Box>
      )}
      {!isLoading && (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {renderIcon()}
          {renderText()}
        </Box>
      )}
    </Button>
  );
};

export default CustomButton;
